import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { 
  Search, 
  Home, 
  Music, 
  Upload, 
  LayoutDashboard, 
  User, 
  Film, 
  Trophy, 
  Mic2, 
  Building2, 
  Users, 
  Calendar, 
  Radio, 
  Settings, 
  Zap,
  X,
  Command,
  Medal,
  ListMusic,
  Gift,
  Video
} from 'lucide-react';
import { cn } from '../lib/utils';
import { lock, unlock } from '../utils/scrollLock';

// Navigation items with search keywords
const NAVIGATION_ITEMS = [
  // Main Navigation
  { 
    name: 'Feed', 
    path: '/feed', 
    icon: Home, 
    keywords: ['feed', 'home', 'timeline', 'posts'],
    requiresAuth: true,
    category: 'Main'
  },
  { 
    name: 'Discover', 
    path: '/home', 
    icon: Music, 
    keywords: ['discover', 'explore', 'browse', 'music', 'artists'],
    category: 'Main'
  },
  { 
    name: 'Soundstage Clips', 
    path: '/reels', 
    icon: Film, 
    keywords: ['reels', 'videos', 'short', 'clips', 'tiktok', 'soundstage'],
    category: 'Main'
  },
  { 
    name: 'Charts', 
    path: '/charts', 
    icon: Trophy, 
    keywords: ['charts', 'rankings', 'top', 'trending', 'popular', 'top 20'],
    category: 'Main'
  },
  { 
    name: 'Leaderboard', 
    path: '/leaderboard', 
    icon: Medal, 
    keywords: ['leaderboard', 'rankings', 'top fans', 'supporters'],
    category: 'Main'
  },
  
  // Content
  { 
    name: 'Interviews', 
    path: '/interviews', 
    icon: Video, 
    keywords: ['interviews', 'podcast', 'conversation', 'talk'],
    category: 'Content'
  },
  { 
    name: 'Playlists', 
    path: '/playlists', 
    icon: ListMusic, 
    keywords: ['playlists', 'collections', 'curated', 'mix'],
    category: 'Content'
  },
  { 
    name: 'Live Streams', 
    path: '/live', 
    icon: Zap, 
    keywords: ['live', 'streaming', 'broadcast', 'stream'],
    category: 'Content'
  },
  
  // Community
  { 
    name: 'Battles', 
    path: '/battles', 
    icon: Radio, 
    keywords: ['battles', 'cypher', 'competition', 'versus', 'vs'],
    category: 'Community'
  },
  { 
    name: 'Battle Bot', 
    path: '/battle-bot', 
    icon: Radio, 
    keywords: ['battle bot', 'ai', 'practice', 'cypher bot'],
    category: 'Community'
  },
  { 
    name: 'Events', 
    path: '/events', 
    icon: Calendar, 
    keywords: ['events', 'shows', 'concerts', 'performances', 'gigs'],
    category: 'Community'
  },
  
  // Creatives & Services
  { 
    name: 'Creator Marketplace', 
    path: '/marketplace', 
    icon: Users, 
    keywords: ['creatives', 'professionals', 'services', 'directory', 'marketplace', 'producers', 'designers'],
    category: 'Services'
  },
  { 
    name: 'Studios', 
    path: '/studios', 
    icon: Building2, 
    keywords: ['studios', 'recording', 'studio', 'book', 'session'],
    category: 'Services'
  },
  { 
    name: 'Perks', 
    path: '/perks', 
    icon: Gift, 
    keywords: ['perks', 'benefits', 'rewards', 'subscription'],
    category: 'Services'
  },
  
  // User Actions - Auth Required
  { 
    name: 'Dashboard', 
    path: '/dashboard/artist', 
    icon: LayoutDashboard, 
    keywords: ['dashboard', 'manage', 'admin', 'control', 'my dashboard'],
    requiresAuth: true,
    requiresRole: ['artist', 'owner'],
    category: 'Account'
  },
  { 
    name: 'Upload Track', 
    path: '/dashboard/artist', 
    icon: Upload, 
    keywords: ['upload', 'track', 'song', 'music', 'add track', 'new track', 'submit'],
    requiresAuth: true,
    requiresRole: ['artist', 'owner'],
    category: 'Account',
    tab: 'upload'
  },
  { 
    name: 'Upload Video', 
    path: '/dashboard/artist', 
    icon: Film, 
    keywords: ['upload video', 'music video', 'add video', 'new video'],
    requiresAuth: true,
    requiresRole: ['artist', 'owner'],
    category: 'Account',
    tab: 'upload'
  },
  { 
    name: 'My Profile', 
    path: '/profile/{handle}', 
    icon: User, 
    keywords: ['profile', 'my profile', 'public profile', 'view profile'],
    requiresAuth: true,
    requiresHandle: true,
    category: 'Account'
  },
  { 
    name: 'Settings', 
    path: '/settings', 
    icon: Settings, 
    keywords: ['settings', 'preferences', 'account', 'configuration'],
    requiresAuth: true,
    category: 'Account'
  },
  
  // Info Pages
  { 
    name: 'Pricing', 
    path: '/pricing', 
    icon: Gift, 
    keywords: ['pricing', 'plans', 'subscription', 'upgrade', 'tiers'],
    category: 'Info'
  },
  { 
    name: 'About / Why DMV', 
    path: '/why', 
    icon: Music, 
    keywords: ['about', 'why', 'founder', 'story', 'mission'],
    category: 'Info'
  },
];

export function GlobalSearch({ className }) {
  const { user, isAuthenticated, userRole } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Filter navigation items based on query and user permissions
  const filteredItems = NAVIGATION_ITEMS.filter(item => {
    // Check auth requirement
    if (item.requiresAuth && !isAuthenticated) return false;
    
    // Check role requirement
    if (item.requiresRole && !item.requiresRole.includes(userRole)) return false;
    
    // Check handle requirement
    if (item.requiresHandle && !user?.handle) return false;
    
    // Filter by search query
    if (!query) return true;
    
    const searchLower = query.toLowerCase();
    return (
      item.name.toLowerCase().includes(searchLower) ||
      item.keywords.some(k => k.toLowerCase().includes(searchLower))
    );
  });

  // Group items by category
  const groupedItems = filteredItems.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Open with Cmd/Ctrl + K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      
      // Close with Escape
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Manage scroll lock
  useEffect(() => {
    if (isOpen) {
      lock();
    }
    return () => {
      if (isOpen) {
        unlock();
      }
    };
  }, [isOpen]);

  // Handle navigation
  const handleSelect = (item) => {
    let path = item.path;
    
    // Replace handle placeholder
    if (item.requiresHandle && user?.handle) {
      path = path.replace('{handle}', user.handle);
    }
    
    navigate(path);
    setIsOpen(false);
    setQuery('');
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    const flatItems = Object.values(groupedItems).flat();
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, flatItems.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (flatItems[selectedIndex]) {
          handleSelect(flatItems[selectedIndex]);
        }
        break;
      default:
        break;
    }
  };

  // Reset selected index when query changes - use callback to avoid lint warning
  const queryRef = useRef(query);
  useEffect(() => {
    if (queryRef.current !== query) {
      queryRef.current = query;
      setSelectedIndex(0);
    }
  });

  return (
    <>
      {/* Search Trigger Button */}
      <Button
        variant="ghost"
        onClick={() => setIsOpen(true)}
        className={cn(
          "flex items-center gap-2 text-muted-foreground hover:text-foreground",
          className
        )}
        data-testid="global-search-trigger"
      >
        <Search className="w-4 h-4" />
        <span className="hidden sm:inline">Search navigation...</span>
        <kbd className="hidden md:inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>

      {/* Search Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[100]" data-testid="global-search-modal">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => {
              setIsOpen(false);
              setQuery('');
            }}
          />
          
          {/* Modal */}
          <div className="relative max-w-lg mx-auto mt-20 mx-4">
            <div className="bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
              {/* Search Input */}
              <div className="flex items-center gap-3 p-4 border-b border-border">
                <Search className="w-5 h-5 text-muted-foreground" />
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder="Search for pages, features..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 border-0 bg-transparent focus-visible:ring-0 text-lg"
                  data-testid="global-search-input"
                />
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => {
                    setIsOpen(false);
                    setQuery('');
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Results */}
              <div 
                ref={resultsRef}
                className="max-h-[60vh] overflow-y-auto p-2"
              >
                {Object.entries(groupedItems).length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No results found for &quot;{query}&quot;</p>
                  </div>
                ) : (
                  Object.entries(groupedItems).map(([category, items]) => (
                    <div key={category} className="mb-4">
                      <p className="text-xs font-medium text-muted-foreground px-3 py-2 uppercase tracking-wider">
                        {category}
                      </p>
                      {items.map((item, idx) => {
                        const Icon = item.icon;
                        const flatIndex = Object.values(groupedItems)
                          .flat()
                          .findIndex(i => i === item);
                        const isSelected = flatIndex === selectedIndex;
                        
                        return (
                          <button
                            key={item.path + item.name}
                            onClick={() => handleSelect(item)}
                            className={cn(
                              "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors",
                              isSelected 
                                ? "bg-primary/20 text-primary" 
                                : "hover:bg-muted/30 text-foreground"
                            )}
                            data-testid={`search-result-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <div className={cn(
                              "w-8 h-8 rounded-lg flex items-center justify-center",
                              isSelected ? "bg-primary/20" : "bg-muted/30"
                            )}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{item.name}</p>
                              <p className="text-xs text-muted-foreground line-clamp-1">
                                {item.keywords.slice(0, 3).join(' • ')}
                              </p>
                            </div>
                            {isSelected && (
                              <span className="text-xs text-muted-foreground">↵</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-border p-3 flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-muted/30 rounded">↑</kbd>
                    <kbd className="px-1.5 py-0.5 bg-muted/30 rounded">↓</kbd>
                    Navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-muted/30 rounded">↵</kbd>
                    Select
                  </span>
                </div>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-muted/30 rounded">Esc</kbd>
                  Close
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default GlobalSearch;
